import { Injectable } from "@angular/core";
import { lastValueFrom } from "rxjs";
import {
  Marking,
  MarkingPlace,
  MarkingTypeWithRelations,
} from "src/app/api/models";
import {
  MarkingPlaceControllerService,
  MarkingTypeControllerService,
} from "src/app/api/services";

export interface MarkingSetup {
  hasMarkingPlace: boolean;
  title: string;
  shortcut: string;
  label: string;
}

@Injectable({
  providedIn: "root",
})
export class MarkingService {
  constructor() {}

  /**
   * Generates a MarkingSetup object based on the provided marking.
   *
   * @param marking - The marking object containing various marking positions.
   * @param places - An array of MarkingPlace objects used to retrieve labeling information.
   * @param types - An array of MarkingTypeWithRelations objects used to determine the marking type label.
   * @returns The MarkingSetup object containing the marking place status, title, shortcut, and marking type label.
   */
  getMarkingSetup(
    marking: Marking,
    places: MarkingPlace[],
    types: MarkingTypeWithRelations[]
  ): MarkingSetup {
    // Initialize the setup object with default values.
    const setup: MarkingSetup = {
      hasMarkingPlace: false,
      title: "",
      shortcut: "",
      label: "",
    };

    // Step 1: Determine whether the marking has any defined marking places.
    // The setup.hasMarkingPlace flag is set to true if any marking property is not null.
    setup.hasMarkingPlace =
      marking.horizontal != null ||
      marking.vertical != null ||
      marking.frontback != null ||
      marking.other != null;

    // Step 2: Build an array of title parts by checking each available marking position.
    const titleParts: string[] = [];
    if (marking.horizontal != null) {
      // Retrieve the title label for the horizontal marking.
      titleParts.push(
        this.getMarkingPlaceLabel(marking.horizontal, "title", places)
      );
    }
    if (marking.vertical != null) {
      // Retrieve the title label for the vertical marking.
      titleParts.push(
        this.getMarkingPlaceLabel(marking.vertical, "title", places)
      );
    }
    if (marking.frontback != null) {
      // Retrieve the title label for the frontback marking.
      titleParts.push(
        this.getMarkingPlaceLabel(marking.frontback, "title", places)
      );
    }
    if (marking.other != null) {
      // Retrieve the title label for any other marking position.
      titleParts.push(
        this.getMarkingPlaceLabel(marking.other, "title", places)
      );
    }
    // Join the collected title parts with a space.
    setup.title = titleParts.join(" ");

    // Step 3: Generate a shortcut string if any marking place exists.
    // This concatenates the shortcut labels for each marking position into a single string enclosed in parentheses.
    setup.shortcut = setup.hasMarkingPlace
      ? `(${this.getMarkingPlaceLabel(
          marking.horizontal,
          "shortcut",
          places
        )}${this.getMarkingPlaceLabel(
          marking.vertical,
          "shortcut",
          places
        )}${this.getMarkingPlaceLabel(
          marking.frontback,
          "shortcut",
          places
        )}${this.getMarkingPlaceLabel(marking.other, "shortcut", places)})`
      : "";

    // Step 4: Retrieve the marking type label based on the marking's type ID.
    setup.label = this.getMarkingTypeLabel(marking.markingTypeId, types);

    // Step 5: Return the fully constructed MarkingSetup object.
    return setup;
  }

  /**
   * Gets the label for a marking type by its ID.
   *
   * @param {number} id - The ID of the marking type.
   * @returns {string} - The label of the marking type.
   */
  private getMarkingTypeLabel(
    id: number,
    types?: MarkingTypeWithRelations[]
  ): string {
    const markingType = types.find((a) => a.id === id);
    if (!markingType) return "";
    return markingType.langs[0].name;
  }

  /**
   * Gets the label for a marking place by its ID and type.
   *
   * @param {number} id - The ID of the marking place.
   * @param {"title" | "shortcut"} type - The type of label to get.
   * @returns {string} - The label of the marking place.
   */
  private getMarkingPlaceLabel(
    id: number,
    type: "title" | "shortcut",
    places?: MarkingPlace[]
  ): string {
    const markingPlace = places.find((a) => a.id === id);
    if (!markingPlace) return "";
    switch (type) {
      case "title":
        return markingPlace.titleCS;
      case "shortcut":
        return markingPlace.shortcutCS;
      default:
        return "";
    }
  }
}
